import { InsightData } from 'schemas/action/InsightDataSchema'
import { TimelineAnnotation } from 'schemas/TimelineAnnotationSchema'
import {
  updateTimelineAnnotation,
  deleteTimelineAnnotation,
  addTimelineAnnotation,
  getTimelineAnnotationsByTopicId
} from 'features/topicAnnotations/asyncThunks'
import { usePageConfigUser } from 'hooks/usePageConfig'
import { useAppDispatch, useAppSelector } from 'app/hooks'

import { TimeInterval } from 'enums'
import { selectAnnotationsInfoByTopicId } from 'features/topicAnnotations/selectors'
import { TimelineWidget } from '../../../../types'

const useAnnotationData = (widgetSetting: TimelineWidget, selectedInsights?: InsightData[]) => {
  const dispatch = useAppDispatch()
  const pageConfigUser = usePageConfigUser()
  const { annotations: allAnnotations, isOperating, operatingId } = useAppSelector((state) =>
    selectAnnotationsInfoByTopicId(state, widgetSetting.topicInfos?.[0]?.topicId)
  )

  const { startDatetime, endDatetime, timeInterval } = selectedInsights?.[0] ?? {
    startDatetime: Date.now(),
    endDatetime: Date.now(),
    timeInterval: TimeInterval.CUSTOM
  }

  const annotations = allAnnotations.filter((annotation) => {
    const isOverlap =
      // scenario 1:
      // |---annotation---|
      //    |---selected insighight---|
      (annotation.endDatetime! >= startDatetime! && annotation.endDatetime! <= endDatetime!) ||
      // scenario 2:
      //                |---annotation---|
      // |---selected insighight---|
      (annotation.startDatetime! >= startDatetime! && annotation.startDatetime! <= endDatetime!) ||
      // scenario 3:
      //  |------------annotation------------|
      //    |---selected insighight---|
      (annotation.startDatetime! <= startDatetime! && annotation.endDatetime! >= endDatetime!)
    return isOverlap
  })

  const updateAnnotation = async (annotation: TimelineAnnotation) => {
    await dispatch(updateTimelineAnnotation({ annotation }))
  }

  const deleteAnnotation = async (annotation: TimelineAnnotation) => {
    await dispatch(deleteTimelineAnnotation({ annotation }))
  }

  const addAnnotation = async (annotation: Pick<TimelineAnnotation, 'color' | 'description'>) => {
    const newAnnotation = {
      groupId: widgetSetting.topicInfos![0].topicId,
      description: annotation.description,
      color: annotation.color,
      analysisType: widgetSetting.analysisType,
      owner: pageConfigUser.name,
      startDatetime,
      endDatetime,
      timeInterval
    }
    await dispatch(addTimelineAnnotation({ annotation: newAnnotation }))

    dispatch(
      getTimelineAnnotationsByTopicId({
        topicId: widgetSetting.topicInfos![0].topicId!,
        refresh: true
      })
    )
  }

  const operable = widgetSetting?.selectedTopic?.owner?.id === Number(pageConfigUser.userProfileId)

  return {
    annotations,
    updateAnnotation,
    deleteAnnotation,
    addAnnotation,
    timeZone: pageConfigUser.timeZone,
    isOperating,
    operatingId,
    operable
  }
}

export default useAnnotationData
